import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

function Dashboard() {
  useAuthGuard();

  const [stats, setStats] = useState(null);
  const [scanTrend, setScanTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fmt = (n) => new Intl.NumberFormat().format(n ?? 0);

  const growthPct = (current, prev) => {
    if (!prev || prev === 0) return null;
    const added = current - prev;
    if (added === 0) return 0;
    return ((added / prev) * 100).toFixed(1);
  };

  const GrowthBadge = ({ current, prev }) => {
    const pct = growthPct(current, prev);
    if (pct === null) return null;
    const up = pct > 0;
    const color = up ? '#28a745' : pct < 0 ? '#dc3545' : '#8a94ad';
    return (
      <small style={{ color, fontSize: '0.7rem', marginLeft: 4 }}>
        {up ? '\u2191' : pct < 0 ? '\u2193' : '\u2192'}{Math.abs(pct)}%
      </small>
    );
  };

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const [overviewRes, trendRes] = await Promise.all([
          apiFetch(`${API_BASE_URL}/admin/analytics/overview`, { signal: ac.signal }),
          apiFetch(`${API_BASE_URL}/admin/analytics/scan-trend?days=30`, { signal: ac.signal }),
        ]);
        if (!overviewRes.ok) throw new Error(`HTTP ${overviewRes.status}`);
        if (!trendRes.ok) throw new Error(`HTTP ${trendRes.status}`);
        const overviewData = await overviewRes.json();
        const trendData = await trendRes.json();
        setStats(overviewData.data || overviewData);
        setScanTrend(Array.isArray(trendData.data) ? trendData.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const cards = [
    { label: 'Total Users', key: 'totalUsers', prevKey: 'prevMonthUsers', icon: 'users', color: 'primary' },
    { label: 'Total Stores', key: 'totalStores', prevKey: 'prevMonthStores', icon: 'map-pin', color: 'success' },
    { label: 'Total Posts', key: 'totalPosts', prevKey: 'prevMonthPosts', icon: 'file-text', color: 'info' },
    { label: 'Total Reviews', key: 'totalReviews', icon: 'star', color: 'warning' },
    { label: 'Total Scans', key: 'totalScans', prevKey: 'prevMonthScans', icon: 'camera', color: 'danger' },
    { label: "Today's New Users", key: 'newUsersToday', icon: 'user-plus', color: 'secondary' },
    { label: "Today's New Posts", key: 'newPostsToday', icon: 'edit', color: 'dark' },
    { label: "Today's New Scans", key: 'newScansToday', icon: 'zap', color: 'danger' },
  ];

  const b2bCards = [
    { label: 'Store Accounts', key: 'storeAccounts', icon: 'briefcase', color: 'primary' },
    { label: 'Maker Accounts', key: 'makerAccounts', icon: 'package', color: 'info' },
  ];

  // Inline SVG bar chart for scan trend
  const ScanTrendChart = ({ data }) => {
    if (!data || data.length === 0) return <p className="text-muted small">No trend data</p>;
    const maxCount = Math.max(...data.map((d) => d.count), 1);
    const barW = Math.max(Math.floor(500 / data.length) - 2, 4);
    const chartH = 120;

    return (
      <svg width="100%" viewBox={`0 0 ${data.length * (barW + 2)} ${chartH + 20}`} style={{ maxWidth: 600 }}>
        {data.map((d, i) => {
          const h = (d.count / maxCount) * chartH;
          return (
            <g key={i}>
              <rect
                x={i * (barW + 2)}
                y={chartH - h}
                width={barW}
                height={h || 1}
                fill="#4e73df"
                rx={1}
              >
                <title>{d.date}: {d.count}</title>
              </rect>
              {i % 7 === 0 && (
                <text
                  x={i * (barW + 2) + barW / 2}
                  y={chartH + 14}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#8a94ad"
                >
                  {d.date ? d.date.slice(5) : ''}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">Dashboard</h2>
        <p className="page-subtitle">System Overview</p>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && stats && (
        <>
          {/* Main stat cards */}
          <div className="row g-3">
            {cards.map((card) => (
              <div key={card.key} className="col-12 col-md-6 col-xl-3">
                <div className="card stat-card h-100">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded bg-${card.color} bg-opacity-10`}
                      style={{ width: 40, height: 40, minWidth: 40 }}
                    >
                      <i
                        data-feather={card.icon}
                        className={`text-${card.color}`}
                        style={{ width: 18, height: 18 }}
                      ></i>
                    </div>
                    <div>
                      <h4 className="mb-0 fw-bold" style={{ fontSize: '1.25rem' }}>
                        {fmt(stats[card.key])}
                        {card.prevKey && <GrowthBadge current={stats[card.key]} prev={stats[card.prevKey]} />}
                      </h4>
                      <small style={{ color: '#8a94ad', fontSize: '0.75rem' }}>{card.label}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* B2B account cards */}
          <h6 className="mt-4 mb-2" style={{ color: '#525b75', fontSize: '0.8rem' }}>B2B Accounts</h6>
          <div className="row g-3 mb-4">
            {b2bCards.map((card) => (
              <div key={card.key} className="col-12 col-md-6 col-xl-3">
                <div className="card stat-card h-100">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div
                      className={`d-flex align-items-center justify-content-center rounded bg-${card.color} bg-opacity-10`}
                      style={{ width: 40, height: 40, minWidth: 40 }}
                    >
                      <i
                        data-feather={card.icon}
                        className={`text-${card.color}`}
                        style={{ width: 18, height: 18 }}
                      ></i>
                    </div>
                    <div>
                      <h4 className="mb-0 fw-bold" style={{ fontSize: '1.25rem' }}>{fmt(stats[card.key])}</h4>
                      <small style={{ color: '#8a94ad', fontSize: '0.75rem' }}>{card.label}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scan Trend + Top tables row */}
          <div className="row g-3">
            {/* Scan Trend */}
            <div className="col-12 col-xl-6">
              <div className="card h-100">
                <div className="card-header bg-white py-2">
                  <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Scan Trend (30 days)</h6>
                </div>
                <div className="card-body">
                  <ScanTrendChart data={scanTrend} />
                </div>
              </div>
            </div>

            {/* Top 5 Prizes */}
            <div className="col-12 col-xl-3">
              <div className="card h-100">
                <div className="card-header bg-white py-2">
                  <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Top 5 Prizes</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {(stats.topPrizes || []).map((p, i) => (
                        <tr key={i}>
                          <td className="ps-3 small" style={{ width: 24, color: '#8a94ad' }}>{i + 1}</td>
                          <td className="small fw-medium">{p.prizeName}</td>
                          <td className="text-end pe-3 small">{fmt(p.scanCount)}</td>
                        </tr>
                      ))}
                      {(!stats.topPrizes || stats.topPrizes.length === 0) && (
                        <tr><td colSpan={3} className="text-center py-3 small text-muted">No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Top 5 Stores */}
            <div className="col-12 col-xl-3">
              <div className="card h-100">
                <div className="card-header bg-white py-2">
                  <h6 className="mb-0" style={{ fontSize: '0.875rem' }}>Top 5 Stores</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {(stats.topStores || []).map((s, i) => (
                        <tr key={i}>
                          <td className="ps-3 small" style={{ width: 24, color: '#8a94ad' }}>{i + 1}</td>
                          <td className="small fw-medium">{s.storeName}</td>
                          <td className="text-end pe-3 small">{fmt(s.scanCount)}</td>
                        </tr>
                      ))}
                      {(!stats.topStores || stats.topStores.length === 0) && (
                        <tr><td colSpan={3} className="text-center py-3 small text-muted">No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

export default Dashboard;
